(function () {
  const containerSelector = '#ProductGridContainer';
  const gridSelector = 'ul#product-grid';
  const paginationSelector = '.pagination-wrapper';
  const nextLinkSelector = '.pagination__item--prev[href]';
  const sentinelClass = 'infinite-scroll-sentinel';
  const statusClass = 'infinite-scroll-status';
  const fallbackClass = 'infinite-scroll-fallback';

  class InfiniteProductGrid {
    constructor(container) {
      this.container = container;
      this.generation = 0;
      this.loading = false;
      this.finished = false;
      this.abortController = null;
      this.observer = null;
      this.mutationObserver = null;
      this.onScroll = this.checkScrollPosition.bind(this);
      this.setup();
      this.observeContainerChanges();
    }

    setup() {
      this.generation += 1;
      this.abortController?.abort();
      this.stopAutoLoading();
      this.removeDynamicElements();

      this.loading = false;
      this.finished = false;
      this.grid = this.container.querySelector(gridSelector);
      this.pagination = this.container.querySelector(paginationSelector);
      const requestedMode = this.container.dataset.infiniteMode;
      this.mode = ['search', 'sequence', 'collection'].includes(requestedMode) ? requestedMode : 'paginate';
      this.discoveryUrl = this.mode === 'paginate' ? '' : this.container.dataset.discoveryUrl || '';
      this.primaryPageLimit =
        this.mode === 'sequence' ? Math.max(1, Number.parseInt(this.container.dataset.primaryPageLimit, 10) || 1) : 0;
      this.primaryPagesLoaded = 0;
      this.loadedPageUrls = new Set();
      const nextLink = this.pagination?.querySelector(nextLinkSelector);

      if (this.mode !== 'paginate') {
        this.stage = nextLink ? (this.mode === 'search' ? 'search' : 'primary') : 'discovery';
        this.nextUrl = nextLink?.href || this.discoveryUrl;
      } else {
        this.stage = 'paginate';
        this.nextUrl = nextLink?.href || '';
      }

      if (!this.grid || !this.nextUrl) {
        this.pagination?.removeAttribute('hidden');
        return;
      }

      this.seenProducts = new Set();
      (this.container.dataset.excludedProducts || '')
        .split('|')
        .map((handle) => handle.trim().toLowerCase())
        .filter(Boolean)
        .forEach((handle) => this.seenProducts.add(`/products/${handle}`));
      Array.from(this.grid.children).forEach((item) => {
        const productKey = this.getProductKey(item);
        if (productKey) this.seenProducts.add(productKey);
      });

      this.createDynamicElements();
      this.pagination?.setAttribute('hidden', '');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) this.loadNextPage();
          },
          { rootMargin: '900px 0px 1200px' }
        );
        this.observer.observe(this.sentinel);
      }

      requestAnimationFrame(() => this.checkScrollPosition());
    }

    observeContainerChanges() {
      this.mutationObserver = new MutationObserver(() => {
        const currentGrid = this.container.querySelector(gridSelector);
        if (currentGrid !== this.grid) this.setup();
      });
      this.mutationObserver.observe(this.container, { childList: true, subtree: true });
    }

    createDynamicElements() {
      this.status = document.createElement('p');
      this.status.className = `visually-hidden ${statusClass}`;
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');

      this.fallback = document.createElement('a');
      this.fallback.className = `button button--secondary hidden ${fallbackClass}`;
      this.fallback.textContent = this.container.dataset.fallbackText || '';
      this.fallback.hidden = true;

      this.sentinel = document.createElement('span');
      this.sentinel.className = sentinelClass;
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.sentinel.style.cssText = 'display:block;width:100%;height:1px;clear:both;';

      this.container.append(this.status, this.fallback, this.sentinel);
    }

    removeDynamicElements() {
      this.container.querySelector(`.${sentinelClass}`)?.remove();
      this.container.querySelector(`.${statusClass}`)?.remove();
      this.container.querySelector(`.${fallbackClass}`)?.remove();
      this.sentinel = null;
      this.status = null;
      this.fallback = null;
    }

    stopAutoLoading() {
      this.observer?.disconnect();
      this.observer = null;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    getProductKey(item) {
      const productLink = item.querySelector('a[href*="/products/"]');
      if (!productLink) return '';

      try {
        const productUrl = new URL(productLink.getAttribute('href'), window.location.origin);
        const productPath = productUrl.pathname.match(/\/products\/[^/]+/i)?.[0];
        return productPath ? productPath.toLowerCase() : '';
      } catch (error) {
        return '';
      }
    }

    prepareItem(item, sourceGrid, sourceUrl) {
      item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
      item.removeAttribute('data-cascade');
      item.style.removeProperty('--animation-order');
      if (this.mode === 'collection') {
        item.querySelectorAll('img').forEach((image) => image.setAttribute('loading', 'lazy'));
      }
      if (this.mode === 'collection' && sourceGrid?.dataset.id && sourceUrl) {
        const url = new URL(sourceUrl, window.location.origin);
        url.searchParams.delete('section_id');
        item.querySelectorAll('quick-add-bulk').forEach((card) => {
          card.dataset.sourceSectionId = sourceGrid.dataset.id;
          card.dataset.sourceUrl = `${url.pathname}${url.search}`;
        });
      }
      return item;
    }

    isPrimaryStage() {
      return this.stage === 'search' || this.stage === 'primary';
    }

    appendItems(nextGrid, sourceUrl) {
      if (!this.grid.dataset.id && nextGrid.dataset.id) {
        this.grid.dataset.id = nextGrid.dataset.id;
      }

      const fragment = document.createDocumentFragment();
      let appended = 0;

      Array.from(nextGrid.children).forEach((item) => {
        if (item.tagName !== 'LI') return;
        const productKey = this.getProductKey(item);
        if (productKey && this.seenProducts.has(productKey)) return;
        if (this.stage === 'discovery' && !productKey) return;
        if (productKey) this.seenProducts.add(productKey);
        fragment.appendChild(this.prepareItem(item, nextGrid, sourceUrl));
        appended += 1;
      });

      if (appended && this.mode === 'collection' && this.stage === 'discovery') {
        this.appendDiscoveryBanner();
      }
      this.grid.appendChild(fragment);
      return appended;
    }

    appendDiscoveryBanner() {
      if (this.grid.querySelector('[data-catalog-divider]')) return;
      const template = this.container.querySelector('template[data-catalog-divider-template]');
      if (template) this.grid.appendChild(template.content.cloneNode(true));
    }

    updatePagination(nextPagination) {
      if (this.pagination && nextPagination) {
        this.pagination.replaceWith(nextPagination);
        this.pagination = nextPagination;
        this.pagination.setAttribute('hidden', '');
      } else if (this.pagination) {
        this.pagination.remove();
        this.pagination = null;
      }
    }

    startDiscovery() {
      this.stage = 'discovery';
      this.nextUrl = this.discoveryUrl;
      this.pagination?.remove();
      this.pagination = null;
      if (!this.nextUrl) this.finish();
    }

    async loadNextPage() {
      if (this.loading || this.finished || !this.nextUrl) return;

      this.loading = true;
      this.container.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = this.container.dataset.loadingText || '';
      const requestGeneration = this.generation;
      this.abortController = new AbortController();

      try {
        const requestUrl = new URL(this.nextUrl, window.location.origin).toString();
        if (this.mode === 'collection' && this.loadedPageUrls.has(requestUrl)) {
          throw new Error('Repeated collection page');
        }
        const response = await fetch(requestUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (requestGeneration !== this.generation) return;

        const nextContainer = html.querySelector(containerSelector);
        const nextGrid = nextContainer?.querySelector(gridSelector);
        const nextPagination = nextContainer?.querySelector(paginationSelector);
        const nextLink = nextPagination?.querySelector(nextLinkSelector);

        if (!nextGrid) {
          if (this.mode === 'collection') throw new Error('Missing collection product grid');
          if (this.isPrimaryStage()) {
            this.startDiscovery();
          } else {
            this.finish();
          }
          return;
        }

        this.loadedPageUrls.add(requestUrl);
        this.appendItems(nextGrid, response.url || requestUrl);

        if (this.isPrimaryStage()) {
          if (this.stage === 'primary') this.primaryPagesLoaded += 1;
          this.updatePagination(nextPagination);
          if (this.mode === 'sequence' && this.stage === 'primary' && this.primaryPagesLoaded >= this.primaryPageLimit) {
            this.startDiscovery();
          } else if (nextLink) {
            this.nextUrl = nextLink.href;
          } else {
            this.startDiscovery();
          }
        } else {
          this.updatePagination(nextPagination);
          this.nextUrl = nextLink?.href || '';
          if (!this.nextUrl) this.finish();
        }

        if (!this.finished && this.status) {
          this.status.textContent = this.container.dataset.loadedText || '';
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.pauseWithFallback();
      } finally {
        if (requestGeneration === this.generation) {
          this.loading = false;
          this.container.setAttribute('aria-busy', 'false');
          requestAnimationFrame(() => this.checkScrollPosition());
        }
      }
    }

    pauseWithFallback() {
      this.finished = true;
      this.stopAutoLoading();
      this.sentinel?.remove();
      this.sentinel = null;
      if (this.status) this.status.textContent = this.container.dataset.errorText || '';

      if (this.pagination) {
        this.pagination.removeAttribute('hidden');
      } else if (this.fallback) {
        this.fallback.href = this.nextUrl || this.discoveryUrl;
        this.fallback.classList.remove('hidden');
        this.fallback.hidden = false;
      }
    }

    finish() {
      this.finished = true;
      this.nextUrl = '';
      this.stopAutoLoading();
      this.sentinel?.remove();
      this.sentinel = null;
      this.pagination?.remove();
      this.pagination = null;
      this.fallback?.remove();
      this.fallback = null;
      if (this.status) this.status.textContent = this.container.dataset.completeText || '';
    }

    checkScrollPosition() {
      if (this.loading || this.finished || !this.nextUrl || !this.sentinel) return;
      if (this.sentinel.getBoundingClientRect().top < window.innerHeight + 1200) this.loadNextPage();
    }
  }

  function initInfiniteScroll() {
    document.querySelectorAll(containerSelector).forEach((container) => {
      if (container.dataset.infiniteScrollInitialized === 'true') return;
      container.dataset.infiniteScrollInitialized = 'true';
      new InfiniteProductGrid(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }
})();
